import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  VStack,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import Lottie from "react-lottie";
import lightOverlayAnimationData from "../assets/loginFormBgLight.json";
import darkOverlayAnimationData from "../assets/loginFormBgDark.json";
import lightAnimationData from "../assets/BgLight.json";
import darkAnimationData from "../assets/BgDark.json";
import { ToggleColorModeButton } from "../theme/ToggleColorModeButton";
import { LoadingAnimation } from "./LoadingAnimation";
import { passwordResetAPI } from "../APIs/passwordResetAPI";

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState("");
  const [isValidToken, setIsValidToken] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { colorMode } = useColorMode();

  const textColor = useColorModeValue("gray.800", "gray.100");

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      validateResetToken(tokenFromUrl);
    } else {
      setIsValidatingToken(false);
      setIsValidToken(false);
    }
  }, [searchParams]);

  const validateResetToken = async (resetToken) => {
    try {
      await passwordResetAPI.validateResetToken(resetToken);
      setIsValidToken(true);
    } catch (error) {
      console.error("Token validation error:", error);
      setIsValidToken(false);
      toast({
        title: "Invalid Reset Link",
        description: "This password reset link is invalid or has expired.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsValidatingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast({
        title: "All Fields Required",
        description: "Please fill in all password fields.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters long.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Additional password strength validation
    if (!/(?=.*[a-z])/.test(password)) {
      toast({
        title: "Password Too Weak",
        description: "Password must contain at least one lowercase letter.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      toast({
        title: "Password Too Weak",
        description: "Password must contain at least one uppercase letter.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!/(?=.*\d)/.test(password)) {
      toast({
        title: "Password Too Weak",
        description: "Password must contain at least one number.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure both passwords are the same.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      await passwordResetAPI.resetPassword(token, password);
      
      setIsSuccess(true);
      toast({
        title: "Password Reset Successful",
        description: "Your password has been updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Password reset error:", error);
      toast({
        title: "Reset Failed",
        description: error.response?.data?.message || "Failed to reset password. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const baseAnimationData =
    colorMode === "light" ? lightAnimationData : darkAnimationData;
  const overlayAnimationData =
    colorMode === "light"
      ? lightOverlayAnimationData
      : darkOverlayAnimationData;

  const baseOptions = {
    loop: false,
    autoplay: true,
    animationData: baseAnimationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const overlayOptions = {
    loop: false,
    autoplay: true,
    animationData: overlayAnimationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid meet",
    },
  };

  if (isValidatingToken) {
    return (
      <Box position="relative" height="100vh" width="100vw">
        <LoadingAnimation size="large" />
      </Box>
    );
  }

  if (!isValidToken) {
    return (
      <Box position="relative" height="100vh" width="100vw">
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          overflow="hidden"
          pointerEvents={"none"}
        >
          <Box position="absolute" width="full" height="full">
            <Lottie options={baseOptions} />
          </Box>
          <Box position="absolute" width="full" height="full">
            <Lottie options={overlayOptions} />
          </Box>
        </Box>
        
        <ToggleColorModeButton
          width="24px"
          height="24px"
          iconButtonProps={{
            position: "absolute",
            top: "5",
            right: "5",
          }}
        />

        <VStack
          align="stretch"
          spacing={4}
          p={8}
          rounded="lg"
          maxWidth="400px"
          margin="auto"
          position="relative"
          top="50%"
          transform="translateY(-50%)"
          color={textColor}
        >
          <Text fontSize="2xl" fontWeight="bold" alignSelf="center" color="red.500">
            Invalid Reset Link
          </Text>
          <Text textAlign="center" color="gray.600" fontSize="sm">
            This password reset link is invalid or has expired.
          </Text>
          <Text textAlign="center" color="gray.500" fontSize="xs">
            Please request a new password reset link.
          </Text>
          
          <Button
            leftIcon={<ArrowBackIcon />}
            colorScheme="blue"
            onClick={handleBackToLogin}
            alignSelf="center"
          >
            Back to Login
          </Button>
        </VStack>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box position="relative" height="100vh" width="100vw">
        <LoadingAnimation size="large" />
      </Box>
    );
  }

  return (
    <Box position="relative" height="100vh" width="100vw">
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        overflow="hidden"
        pointerEvents={"none"}
      >
        <Box position="absolute" width="full" height="full">
          <Lottie options={baseOptions} />
        </Box>
        <Box position="absolute" width="full" height="full">
          <Lottie options={overlayOptions} />
        </Box>
      </Box>
      
      <ToggleColorModeButton
        width="24px"
        height="24px"
        iconButtonProps={{
          position: "absolute",
          top: "5",
          right: "5",
        }}
      />

      <VStack
        align="stretch"
        spacing={4}
        p={8}
        rounded="lg"
        maxWidth="400px"
        margin="auto"
        position="relative"
        top="50%"
        transform="translateY(-50%)"
        color={textColor}
      >
        {!isSuccess ? (
          <>
            <Text fontSize="2xl" fontWeight="bold" alignSelf="center">
              Reset Your Password
            </Text>
            <Text textAlign="center" color="gray.600" fontSize="sm">
              Enter your new password below.
            </Text>
            
            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl id="password" isRequired>
                  <FormLabel>New Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={password}
                      variant="filled"
                      borderRadius="full"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <InputRightElement>
                      <IconButton
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        size="sm"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Password must be at least 8 characters with uppercase, lowercase, and number
                  </Text>
                </FormControl>
                
                <FormControl id="confirmPassword" isRequired>
                  <FormLabel>Confirm New Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      variant="filled"
                      borderRadius="full"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <InputRightElement>
                      <IconButton
                        icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        variant="ghost"
                        size="sm"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                
                <Button
                  type="submit"
                  colorScheme="blue"
                  w="full"
                  p={4}
                  borderRadius="full"
                  fontSize="md"
                  fontWeight="bold"
                  boxShadow="md"
                  _hover={{ boxShadow: "lg" }}
                  isLoading={isLoading}
                  loadingText="Resetting..."
                >
                  Reset Password
                </Button>
              </VStack>
            </form>
          </>
        ) : (
          <>
            <Text fontSize="2xl" fontWeight="bold" alignSelf="center" color="green.500">
              Password Reset Successful!
            </Text>
            <Text textAlign="center" color="gray.600" fontSize="sm">
              Your password has been updated successfully.
            </Text>
            <Text textAlign="center" color="gray.500" fontSize="xs">
              You can now log in with your new password.
            </Text>
          </>
        )}
        
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={handleBackToLogin}
          alignSelf="center"
          color="blue.500"
          _hover={{ bg: "blue.50" }}
        >
          Back to Login
        </Button>
      </VStack>
    </Box>
  );
};

export default ResetPasswordPage;
